export interface InsuranceCompany {
  value: string;
  label: string;
  category: string;
  availablePlanTypes: string[]; // References PlanType value fields
  searchTerms?: string[]; // Additional search aliases
}

export interface PlanType {
  value: string;
  label: string;
  description: string;
}

export interface InsurerCriteriaInfo {
  primaryCriteria:
    | "interqual"
    | "mcg"
    | "mixed"
    | "cms"
    | "proprietary"
    | "tricare"
    | "va"
    | "state_wc"
    | "unknown";
  notes: string;
  tips?: string[];
}

// ============================================================================
// PLAN TYPES
// ============================================================================

export const PLAN_TYPES: PlanType[] = [
  // Commercial Plan Types
  {
    value: "hmo",
    label: "HMO",
    description: "Health Maintenance Organization",
  },
  {
    value: "ppo",
    label: "PPO",
    description: "Preferred Provider Organization",
  },
  {
    value: "epo",
    label: "EPO",
    description: "Exclusive Provider Organization",
  },
  { value: "pos", label: "POS", description: "Point of Service" },
  {
    value: "hdhp",
    label: "HDHP",
    description: "High Deductible Health Plan",
  },
  {
    value: "indemnity",
    label: "Indemnity",
    description: "Traditional Fee-for-Service",
  },

  // Medicare Plan Types
  {
    value: "medicare_original",
    label: "Medicare Original",
    description: "Traditional Medicare (Part A/B)",
  },
  {
    value: "medicare_advantage_hmo",
    label: "Medicare Advantage HMO",
    description: "Medicare Part C - HMO",
  },
  {
    value: "medicare_advantage_ppo",
    label: "Medicare Advantage PPO",
    description: "Medicare Part C - PPO",
  },
  {
    value: "medicare_advantage_pffs",
    label: "Medicare Advantage PFFS",
    description: "Medicare Part C - Private Fee-for-Service",
  },
  {
    value: "medicare_snp",
    label: "Medicare SNP",
    description: "Special Needs Plan (Chronic, Dual, or Institutional)",
  },
  {
    value: "medicare_part_d",
    label: "Medicare Part D",
    description: "Prescription Drug Plan",
  },
  {
    value: "medigap",
    label: "Medigap/Supplement",
    description: "Medicare Supplement Insurance",
  },

  // Medicaid Plan Types
  { value: "medicaid_ffs", label: "Medicaid FFS", description: "State Medicaid Fee-for-Service" },
  {
    value: "medicaid_mco",
    label: "Medicaid MCO",
    description: "Medicaid Managed Care Organization",
  },

  // Dual Eligible
  {
    value: "dual_eligible",
    label: "Dual Eligible",
    description: "Medicare + Medicaid (Medi-Medi)",
  },
  {
    value: "dsnp",
    label: "D-SNP",
    description: "Dual Eligible Special Needs Plan",
  },

  // TRICARE Plan Types
  {
    value: "tricare_prime",
    label: "TRICARE Prime",
    description: "HMO-style military health plan",
  },
  {
    value: "tricare_select",
    label: "TRICARE Select",
    description: "PPO-style military health plan",
  },
  {
    value: "tricare_for_life",
    label: "TRICARE For Life",
    description: "Medicare wraparound for military retirees",
  },
  {
    value: "tricare_reserve",
    label: "TRICARE Reserve Select",
    description: "For Selected Reserve members",
  },
  {
    value: "tricare_young_adult",
    label: "TRICARE Young Adult",
    description: "For adult children of military",
  },

  // VA Plan Types
  {
    value: "va_community_care",
    label: "VA Community Care",
    description: "VA-authorized care at non-VA facilities",
  },
  {
    value: "va_direct",
    label: "VA Direct Care",
    description: "Care at VA facilities",
  },

  // CHAMPVA
  {
    value: "champva",
    label: "CHAMPVA",
    description: "Civilian Health and Medical Program of VA",
  },

  // Federal Employee
  {
    value: "fehb_hmo",
    label: "FEHB HMO",
    description: "Federal Employee Health Benefits - HMO",
  },
  {
    value: "fehb_ppo",
    label: "FEHB PPO",
    description: "Federal Employee Health Benefits - PPO",
  },
  {
    value: "fehb_hdhp",
    label: "FEHB HDHP",
    description: "Federal Employee Health Benefits - HDHP",
  },

  // Other Plan Types
  {
    value: "exchange",
    label: "ACA Exchange",
    description: "Marketplace/Exchange Plan",
  },
  {
    value: "self_funded",
    label: "Self-Funded/ERISA",
    description: "Employer Self-Funded Plan",
  },
  {
    value: "workers_comp",
    label: "Workers' Comp",
    description: "Workers' Compensation",
  },
  {
    value: "auto_pip",
    label: "Auto/PIP",
    description: "Auto Insurance Personal Injury Protection",
  },
  {
    value: "short_term",
    label: "Short-Term",
    description: "Short-Term Limited Duration Insurance",
  },
  { value: "unknown", label: "Unknown", description: "Not sure / Other" },
];

// ============================================================================
// STANDARD PLAN TYPE GROUPS (for easy reference)
// ============================================================================

const COMMERCIAL_PLANS = ["hmo", "ppo", "epo", "pos", "hdhp", "indemnity"];
const COMMERCIAL_STANDARD = ["hmo", "ppo", "epo", "pos", "hdhp"];
const MEDICARE_ADVANTAGE_PLANS = [
  "medicare_advantage_hmo",
  "medicare_advantage_ppo",
  "medicare_advantage_pffs",
  "medicare_snp",
];
const MEDICAID_PLANS = ["medicaid_ffs", "medicaid_mco"];
const DUAL_PLANS = ["dual_eligible", "dsnp"];
const TRICARE_PLANS = [
  "tricare_prime",
  "tricare_select",
  "tricare_for_life",
  "tricare_reserve",
  "tricare_young_adult",
];
const VA_PLANS = ["va_community_care", "va_direct"];
const FEHB_PLANS = ["fehb_hmo", "fehb_ppo", "fehb_hdhp"];

// ============================================================================
// INSURANCE CATEGORIES
// ============================================================================

export const INSURANCE_CATEGORIES = [
  "National",
  "Government",
  "Regional Blues",
  "Other Major",
  "Medicaid MCOs",
  "Regional",
];

// ============================================================================
// INSURANCE COMPANIES WITH AVAILABLE PLAN TYPES
// ============================================================================

export const INSURANCE_COMPANIES: InsuranceCompany[] = [
  // ==========================================================================
  // NATIONAL CARRIERS
  // ==========================================================================
  {
    value: "aetna",
    label: "Aetna",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      "dsnp",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["aetna health", "cvs aetna", "cvs health"],
  },
  {
    value: "anthem",
    label: "Anthem Blue Cross Blue Shield",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      "dsnp",
      "exchange",
      "self_funded",
      ...FEHB_PLANS,
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "anthem"],
  },
  {
    value: "bcbs",
    label: "Blue Cross Blue Shield (Other/Unknown)",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      "dsnp",
      "exchange",
      "self_funded",
      ...FEHB_PLANS,
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "cigna",
    label: "Cigna",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["cigna healthcare", "evernorth"],
  },
  {
    value: "humana",
    label: "Humana",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      "medigap",
      "dsnp",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["humana health", "humana healthcare"],
  },
  {
    value: "kaiser",
    label: "Kaiser Permanente",
    category: "National",
    availablePlanTypes: [
      "hmo", // Kaiser is HMO-only for commercial
      "medicare_advantage_hmo",
      "medicare_snp",
      "medicaid_mco",
      "dsnp",
      "exchange",
    ],
    searchTerms: ["kaiser foundation", "kp"],
  },
  {
    value: "united",
    label: "UnitedHealthcare",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      "medigap",
      ...MEDICAID_PLANS,
      ...DUAL_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["uhc", "united health care", "united healthcare", "optum"],
  },
  {
    value: "elevance",
    label: "Elevance Health (Anthem parent)",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      "dsnp",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["anthem", "blue cross", "blue shield", "wellpoint"],
  },
  {
    value: "cvs_aetna",
    label: "CVS Health / Aetna",
    category: "National",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      ...MEDICAID_PLANS,
      "dsnp",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["aetna health", "cvs aetna", "cvs health"],
  },

  // ==========================================================================
  // GOVERNMENT PROGRAMS
  // ==========================================================================
  {
    value: "medicare_original",
    label: "Medicare (Original/Traditional)",
    category: "Government",
    availablePlanTypes: ["medicare_original", "medigap", "medicare_part_d"],
    searchTerms: ["cms", "centers for medicare"],
  },
  {
    value: "medicare_advantage",
    label: "Medicare Advantage (Plan Unknown)",
    category: "Government",
    availablePlanTypes: [...MEDICARE_ADVANTAGE_PLANS, "medicare_part_d"],
    searchTerms: ["cms", "centers for medicare"],
  },
  {
    value: "medicaid",
    label: "Medicaid (State Program)",
    category: "Government",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
    searchTerms: ["cms", "state medicaid"],
  },
  {
    value: "tricare",
    label: "TRICARE",
    category: "Government",
    availablePlanTypes: TRICARE_PLANS,
    searchTerms: ["military", "defense health agency", "dha", "military health"],
  },
  {
    value: "va",
    label: "VA Health Care",
    category: "Government",
    availablePlanTypes: VA_PLANS,
    searchTerms: ["veterans", "veterans administration", "veterans affairs", "va healthcare"],
  },
  {
    value: "champva",
    label: "CHAMPVA",
    category: "Government",
    availablePlanTypes: ["champva"],
    searchTerms: ["veterans", "civilian health", "va"],
  },
  {
    value: "ihs",
    label: "Indian Health Service",
    category: "Government",
    availablePlanTypes: ["unknown"], // IHS is direct care, not a plan type
  },
  {
    value: "fep",
    label: "Federal Employee Program (FEP Blue)",
    category: "Government",
    availablePlanTypes: FEHB_PLANS,
  },

  // ==========================================================================
  // REGIONAL BLUES
  // ==========================================================================
  {
    value: "bcbs_al",
    label: "BCBS of Alabama",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_az",
    label: "BCBS of Arizona",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ar",
    label: "Arkansas BCBS",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ca_anthem",
    label: "Anthem Blue Cross (CA)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "anthem"],
  },
  {
    value: "blue_shield_ca",
    label: "Blue Shield of California",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "bsc"],
  },
  {
    value: "bcbs_co",
    label: "Anthem BCBS of Colorado",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ct",
    label: "Anthem BCBS of Connecticut",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_de",
    label: "Highmark BCBS Delaware",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_fl",
    label: "Florida Blue (BCBS FL)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "florida blue"],
  },
  {
    value: "bcbs_ga",
    label: "BCBS of Georgia (Anthem)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "hmsa",
    label: "HMSA (Hawaii)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "hmsa", "hawaii medical"],
  },
  {
    value: "bcbs_id",
    label: "Blue Cross of Idaho",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_il",
    label: "BCBS of Illinois",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_in",
    label: "Anthem BCBS of Indiana",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ia",
    label: "Wellmark BCBS (Iowa)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ks",
    label: "BCBS of Kansas",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ky",
    label: "Anthem BCBS of Kentucky",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_la",
    label: "BCBS of Louisiana",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_me",
    label: "Anthem BCBS of Maine",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "carefirst",
    label: "CareFirst BCBS (MD/DC/VA)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
      ...FEHB_PLANS,
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "carefirst"],
  },
  {
    value: "bcbs_ma",
    label: "BCBS of Massachusetts",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_mi",
    label: "BCBS of Michigan",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_mn",
    label: "BCBS of Minnesota",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ms",
    label: "BCBS of Mississippi",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_mo",
    label: "Anthem BCBS of Missouri",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_mt",
    label: "BCBS of Montana",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ne",
    label: "BCBS of Nebraska",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_nv",
    label: "Anthem BCBS of Nevada",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_nh",
    label: "Anthem BCBS of New Hampshire",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "horizon",
    label: "Horizon BCBS of NJ",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "dsnp",
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "bcbs_nm",
    label: "BCBS of New Mexico",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "empire_bcbs",
    label: "Empire BCBS (NY)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "dsnp",
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "bcbs_nc",
    label: "BCBS of North Carolina",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_nd",
    label: "BCBS of North Dakota",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_oh",
    label: "Anthem BCBS of Ohio",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ok",
    label: "BCBS of Oklahoma",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "regence",
    label: "Regence BCBS (OR/WA/UT/ID)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "highmark",
    label: "Highmark BCBS (PA/WV/DE)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      "medigap",
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "independence",
    label: "Independence Blue Cross (PA)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "bcbs_ri",
    label: "BCBS of Rhode Island",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_sc",
    label: "BCBS of South Carolina",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_sd",
    label: "Wellmark BCBS (South Dakota)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_tn",
    label: "BCBS of Tennessee",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_tx",
    label: "BCBS of Texas",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_ut",
    label: "Regence BCBS of Utah",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_vt",
    label: "BCBS of Vermont",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_va",
    label: "Anthem BCBS of Virginia",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "premera",
    label: "Premera Blue Cross (WA/AK)",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs", "premera"],
  },
  {
    value: "bcbs_wv",
    label: "Highmark BCBS West Virginia",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_wi",
    label: "Anthem BCBS of Wisconsin",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },
  {
    value: "bcbs_wy",
    label: "BCBS of Wyoming",
    category: "Regional Blues",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
    searchTerms: ["blue cross", "blue shield", "blue cross blue shield", "bcbs"],
  },

  // ==========================================================================
  // OTHER MAJOR INSURERS
  // ==========================================================================
  {
    value: "centene",
    label: "Centene",
    category: "Other Major",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      ...DUAL_PLANS,
      "exchange",
    ],
  },
  {
    value: "molina",
    label: "Molina Healthcare",
    category: "Other Major",
    availablePlanTypes: [
      ...MEDICARE_ADVANTAGE_PLANS,
      ...MEDICAID_PLANS,
      ...DUAL_PLANS,
      "exchange",
    ],
  },
  {
    value: "wellcare",
    label: "WellCare (Centene)",
    category: "Other Major",
    availablePlanTypes: [
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicare_part_d",
      ...MEDICAID_PLANS,
      ...DUAL_PLANS,
    ],
  },
  {
    value: "ambetter",
    label: "Ambetter (Centene)",
    category: "Other Major",
    availablePlanTypes: ["hmo", "ppo", "exchange"],
  },
  {
    value: "oscar",
    label: "Oscar Health",
    category: "Other Major",
    availablePlanTypes: ["hmo", "ppo", "epo", "exchange", ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "clover",
    label: "Clover Health",
    category: "Other Major",
    availablePlanTypes: [...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "bright",
    label: "Bright Health",
    category: "Other Major",
    availablePlanTypes: ["hmo", "ppo", "exchange", ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "devoted",
    label: "Devoted Health",
    category: "Other Major",
    availablePlanTypes: [...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "alignment",
    label: "Alignment Healthcare",
    category: "Other Major",
    availablePlanTypes: [...MEDICARE_ADVANTAGE_PLANS, "dsnp"],
  },
  {
    value: "scan",
    label: "SCAN Health Plan",
    category: "Other Major",
    availablePlanTypes: [...MEDICARE_ADVANTAGE_PLANS, "dsnp"],
  },

  // ==========================================================================
  // MEDICAID MCOs (Centene/Anthem Subsidiaries)
  // ==========================================================================
  {
    value: "sunflower",
    label: "Sunflower Health Plan (KS)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "buckeye",
    label: "Buckeye Health Plan (OH)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "magnolia",
    label: "Magnolia Health (MS)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "superior",
    label: "Superior HealthPlan (TX)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "meridian",
    label: "Meridian Health Plan (MI/IL)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS, ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "peach_state",
    label: "Peach State Health Plan (GA)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "sunshine",
    label: "Sunshine Health (FL)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS, ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "louisiana_healthcare",
    label: "Louisiana Healthcare Connections",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "granite_state",
    label: "Granite State Health Plan (NH)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS],
  },
  {
    value: "home_state",
    label: "Home State Health (MO)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "western_sky",
    label: "Western Sky Community Care (NM)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS],
  },
  {
    value: "coordinated_care",
    label: "Coordinated Care (WA)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS, "exchange"],
  },
  {
    value: "healthy_blue",
    label: "Healthy Blue (Multiple States)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS],
  },
  {
    value: "amerigroup",
    label: "Amerigroup (Anthem)",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS, ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "caresource",
    label: "CareSource",
    category: "Medicaid MCOs",
    availablePlanTypes: [...MEDICAID_PLANS, ...DUAL_PLANS, "exchange", ...MEDICARE_ADVANTAGE_PLANS],
  },

  // ==========================================================================
  // REGIONAL PLANS
  // ==========================================================================
  {
    value: "health_partners",
    label: "HealthPartners (MN/WI)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "geisinger",
    label: "Geisinger Health Plan (PA)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "upmc",
    label: "UPMC Health Plan (PA)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "dsnp",
      "exchange",
    ],
  },
  {
    value: "priority_health",
    label: "Priority Health (MI)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "selecthealth",
    label: "SelectHealth (UT/ID)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "healthfirst",
    label: "Healthfirst (NY)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      ...DUAL_PLANS,
      "exchange",
    ],
  },
  {
    value: "emblemhealth",
    label: "EmblemHealth (NY)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "dsnp",
      "exchange",
    ],
  },
  {
    value: "mvp",
    label: "MVP Health Care (NY/VT)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
    ],
  },
  {
    value: "tufts",
    label: "Tufts Health Plan (MA)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "harvard_pilgrim",
    label: "Harvard Pilgrim (New England)",
    category: "Regional",
    availablePlanTypes: [...COMMERCIAL_STANDARD, "exchange"],
  },
  {
    value: "fallon",
    label: "Fallon Health (MA)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
    ],
  },
  {
    value: "medical_mutual",
    label: "Medical Mutual of Ohio",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
      "self_funded",
    ],
  },
  {
    value: "la_care",
    label: "L.A. Care Health Plan (CA)",
    category: "Regional",
    availablePlanTypes: ["hmo", "medicaid_mco", "dsnp", "exchange"],
  },
  {
    value: "health_net",
    label: "Health Net (CA)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      ...DUAL_PLANS,
      "exchange",
      ...TRICARE_PLANS,
    ],
  },
  {
    value: "cdphp",
    label: "CDPHP (NY)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "medicaid_mco",
      "exchange",
    ],
  },
  {
    value: "connecticare",
    label: "ConnectiCare (CT)",
    category: "Regional",
    availablePlanTypes: [
      ...COMMERCIAL_STANDARD,
      ...MEDICARE_ADVANTAGE_PLANS,
      "exchange",
    ],
  },
  {
    value: "quartz",
    label: "Quartz (WI)",
    category: "Regional",
    availablePlanTypes: [...COMMERCIAL_STANDARD, ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "group_health",
    label: "Group Health Cooperative (WA)",
    category: "Regional",
    availablePlanTypes: ["hmo", ...MEDICARE_ADVANTAGE_PLANS, "medicaid_mco"],
  },
  {
    value: "allina",
    label: "Allina Health Aetna (MN)",
    category: "Regional",
    availablePlanTypes: ["hmo", "ppo", ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "sanford",
    label: "Sanford Health Plan (ND/SD/MN)",
    category: "Regional",
    availablePlanTypes: [...COMMERCIAL_STANDARD, ...MEDICARE_ADVANTAGE_PLANS],
  },
  {
    value: "sutter",
    label: "Sutter Health Plus (CA)",
    category: "Regional",
    availablePlanTypes: ["hmo"],
  },

  // ==========================================================================
  // WORKERS' COMP / AUTO
  // ==========================================================================
  {
    value: "workers_comp_carrier",
    label: "Workers' Compensation Carrier",
    category: "Other Major",
    availablePlanTypes: ["workers_comp"],
  },
  {
    value: "auto_pip_carrier",
    label: "Auto/PIP Insurance Carrier",
    category: "Other Major",
    availablePlanTypes: ["auto_pip"],
  },

  // ==========================================================================
  // OTHER
  // ==========================================================================
  {
    value: "other",
    label: "Other (Not Listed)",
    category: "Regional",
    availablePlanTypes: [...PLAN_TYPES.map((p) => p.value)], // All plan types
  },
];

// ============================================================================
// INSURER CRITERIA INFO
// ============================================================================

export const INSURER_CRITERIA_INFO: Record<string, InsurerCriteriaInfo> = {
  // National Carriers
  united: {
    primaryCriteria: "interqual",
    notes:
      "UnitedHealthcare owns InterQual through Optum/Change Healthcare acquisition.",
    tips: [
      "Emphasize intensity of service (IV meds, monitoring frequency, nursing interventions)",
      "Document day-by-day care requirements",
      "Highlight why outpatient care is insufficient",
      "For MA plans, remind reviewer that CMS criteria take precedence",
    ],
  },
  cigna: {
    primaryCriteria: "mcg",
    notes: "Cigna primarily uses MCG (Milliman Care Guidelines).",
    tips: [
      "Emphasize severity of illness markers",
      "Focus on diagnosis-specific criteria",
      "Document functional impairment clearly",
      "Provide specific lab values and vital signs",
    ],
  },
  aetna: {
    primaryCriteria: "mixed",
    notes:
      "Aetna uses both InterQual and MCG depending on service type and region.",
    tips: [
      "Prepare arguments for both intensity of service and severity of illness",
      "Ask the reviewer which criteria they're using",
      "Document both clinical severity AND service requirements",
    ],
  },
  cvs_aetna: {
    primaryCriteria: "mixed",
    notes:
      "CVS/Aetna uses both InterQual and MCG depending on service type.",
    tips: [
      "Prepare arguments for both intensity of service and severity of illness",
      "Ask the reviewer which criteria they're using",
    ],
  },
  anthem: {
    primaryCriteria: "interqual",
    notes: "Most Anthem/BCBS regions use InterQual criteria.",
    tips: [
      "Focus on intensity of service documentation",
      "Emphasize monitoring and intervention requirements",
      "Document frequency of nursing assessments needed",
    ],
  },
  elevance: {
    primaryCriteria: "interqual",
    notes: "Elevance (Anthem parent) uses InterQual across most products.",
    tips: [
      "Focus on intensity of service documentation",
      "Emphasize monitoring and intervention requirements",
    ],
  },
  humana: {
    primaryCriteria: "mixed",
    notes: "Humana uses both InterQual and MCG criteria sets.",
    tips: [
      "Prepare for both criteria systems",
      "For Medicare Advantage, emphasize CMS coverage requirements",
      "Document both severity and service intensity",
    ],
  },
  kaiser: {
    primaryCriteria: "proprietary",
    notes:
      "Kaiser uses proprietary internal criteria based on their integrated care model.",
    tips: [
      "Reference evidence-based clinical guidelines",
      "Kaiser often responds well to literature citations",
      "Emphasize care coordination benefits",
    ],
  },

  // Government Programs
  medicare_original: {
    primaryCriteria: "cms",
    notes:
      "Uses NCDs and LCDs. Search cms.gov/medicare-coverage-database for specific criteria.",
    tips: [
      "Reference specific LCD/NCD by name and number",
      "Check MAC (Medicare Administrative Contractor) for regional policies",
      "CMS criteria are publicly available and searchable",
    ],
  },
  medicare_advantage: {
    primaryCriteria: "cms",
    notes:
      "MUST follow Traditional Medicare NCDs/LCDs. Per CMS rules, MA plans cannot use InterQual/MCG to deny services covered under Traditional Medicare.",
    tips: [
      "Remind reviewer: MA plans must cover what Original Medicare covers",
      "Reference the specific LCD/NCD",
      "Per CMS 2024 Final Rule, prior auth cannot be more restrictive than FFS Medicare",
    ],
  },
  medicaid: {
    primaryCriteria: "mixed",
    notes:
      "Varies by state. Many use InterQual or MCG, some have state-specific criteria.",
    tips: [
      "Check your state Medicaid manual for specific requirements",
      "Document social determinants of health",
      "Emphasize EPSDT coverage for pediatric cases",
    ],
  },
  tricare: {
    primaryCriteria: "tricare",
    notes: "Uses TRICARE Policy Manual and DoD clinical guidelines.",
    tips: [
      "Reference TRICARE Policy Manual chapters",
      "Military treatment facility alternatives may be relevant",
      "TRICARE has specific rules for referrals and authorizations",
    ],
  },
  va: {
    primaryCriteria: "va",
    notes: "Uses VA/DoD Clinical Practice Guidelines.",
    tips: [
      "Reference VA/DoD CPGs when available",
      "Community Care eligibility has specific criteria",
      "Document why VA facility care is not feasible if applicable",
    ],
  },
  champva: {
    primaryCriteria: "va",
    notes: "Follows VA guidelines but for civilian dependents/survivors.",
    tips: [
      "Similar to VA criteria",
      "Must use VA-authorized providers",
    ],
  },
  fep: {
    primaryCriteria: "mixed",
    notes: "Federal Employee Program uses BCBS guidelines, varies by service.",
    tips: [
      "FEP has its own medical policy manual",
      "Reference FEP-specific policies when available",
    ],
  },

  // Regional Blues - Most use InterQual
  bcbs: {
    primaryCriteria: "interqual",
    notes: "Most BCBS plans use InterQual, but some regions use MCG.",
    tips: [
      "Ask which criteria system is being used",
      "Emphasize intensity of service for InterQual users",
    ],
  },
  bcbs_ma: {
    primaryCriteria: "interqual",
    notes: "BCBS of Massachusetts uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Massachusetts has strong coverage mandates",
    ],
  },
  highmark: {
    primaryCriteria: "mcg",
    notes: "Highmark BCBS switched to MCG criteria in 2023.",
    tips: [
      "Emphasize severity of illness",
      "Focus on diagnosis-specific criteria",
    ],
  },
  horizon: {
    primaryCriteria: "interqual",
    notes: "Horizon BCBS of NJ uses InterQual criteria.",
    tips: [
      "New Jersey has specific coverage mandates",
      "Emphasize intensity of service",
    ],
  },
  carefirst: {
    primaryCriteria: "interqual",
    notes: "CareFirst BCBS uses InterQual criteria.",
    tips: [
      "Covers MD/DC/VA region",
      "Emphasize intensity of service",
    ],
  },
  empire_bcbs: {
    primaryCriteria: "interqual",
    notes: "Empire BCBS (NY) uses InterQual criteria.",
    tips: [
      "New York state has strong coverage mandates",
      "Emphasize intensity of service",
    ],
  },
  bcbs_fl: {
    primaryCriteria: "interqual",
    notes: "Florida Blue uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Florida has specific step therapy requirements",
    ],
  },
  bcbs_tx: {
    primaryCriteria: "interqual",
    notes: "BCBS of Texas uses InterQual criteria.",
    tips: [
      "Texas has independent review organization (IRO) appeal rights",
      "Emphasize intensity of service",
    ],
  },
  bcbs_il: {
    primaryCriteria: "interqual",
    notes: "BCBS of Illinois uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Illinois has specific external review rights",
    ],
  },
  bcbs_mi: {
    primaryCriteria: "interqual",
    notes: "BCBS of Michigan uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Michigan has strong external review rights",
    ],
  },
  bcbs_nc: {
    primaryCriteria: "interqual",
    notes: "BCBS of North Carolina uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
    ],
  },
  independence: {
    primaryCriteria: "interqual",
    notes: "Independence Blue Cross (PA) uses InterQual criteria.",
    tips: [
      "Pennsylvania has specific appeal timeframes",
      "Emphasize intensity of service",
    ],
  },
  premera: {
    primaryCriteria: "interqual",
    notes: "Premera Blue Cross uses InterQual criteria.",
    tips: [
      "Covers WA/AK region",
      "Emphasize intensity of service",
    ],
  },
  regence: {
    primaryCriteria: "interqual",
    notes: "Regence BCBS uses InterQual criteria.",
    tips: [
      "Covers OR/WA/UT/ID region",
      "Emphasize intensity of service",
    ],
  },
  blue_shield_ca: {
    primaryCriteria: "mcg",
    notes: "Blue Shield of California uses MCG criteria.",
    tips: [
      "Emphasize severity of illness",
      "Reference California state mandates when applicable",
    ],
  },

  // Other Major Insurers
  centene: {
    primaryCriteria: "interqual",
    notes: "Centene switched from MCG to InterQual in 2022.",
    tips: [
      "Emphasize intensity of service",
      "Document day-by-day care requirements",
    ],
  },
  wellcare: {
    primaryCriteria: "interqual",
    notes: "WellCare (Centene) uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "For Medicare plans, reference CMS criteria",
    ],
  },
  ambetter: {
    primaryCriteria: "interqual",
    notes: "Ambetter (Centene) uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Exchange plans have specific appeal rights under ACA",
    ],
  },
  molina: {
    primaryCriteria: "interqual",
    notes: "Molina Healthcare uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Strong Medicaid focus - document social determinants",
    ],
  },
  oscar: {
    primaryCriteria: "mixed",
    notes: "Oscar Health uses proprietary algorithms with clinical guidelines.",
    tips: [
      "Reference evidence-based guidelines",
      "Oscar is generally responsive to clinical literature",
    ],
  },
  clover: {
    primaryCriteria: "proprietary",
    notes: "Clover Health uses proprietary AI-driven criteria (Clover Assistant).",
    tips: [
      "Focus on evidence-based clinical guidelines",
      "Medicare Advantage rules apply",
    ],
  },
  devoted: {
    primaryCriteria: "cms",
    notes: "Devoted Health follows CMS Medicare guidelines closely.",
    tips: [
      "Reference LCD/NCD criteria",
      "Emphasize Medicare coverage requirements",
    ],
  },

  // Regional Plans
  health_partners: {
    primaryCriteria: "interqual",
    notes: "HealthPartners uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Minnesota has strong external review rights",
    ],
  },
  geisinger: {
    primaryCriteria: "interqual",
    notes: "Geisinger Health Plan uses InterQual criteria.",
    tips: [
      "Integrated health system - may have internal guidelines",
      "Emphasize intensity of service",
    ],
  },
  upmc: {
    primaryCriteria: "interqual",
    notes: "UPMC Health Plan uses InterQual criteria.",
    tips: [
      "Integrated health system",
      "Emphasize intensity of service",
    ],
  },
  healthfirst: {
    primaryCriteria: "interqual",
    notes: "Healthfirst (NY) uses InterQual criteria.",
    tips: [
      "Reference NY state coverage requirements",
      "Emphasize intensity of service",
    ],
  },
  emblemhealth: {
    primaryCriteria: "interqual",
    notes: "EmblemHealth uses InterQual criteria.",
    tips: [
      "New York state has strong coverage mandates",
      "Emphasize intensity of service",
    ],
  },
  tufts: {
    primaryCriteria: "interqual",
    notes: "Tufts Health Plan uses InterQual criteria.",
    tips: [
      "Massachusetts has strong coverage mandates",
      "Emphasize intensity of service",
    ],
  },
  harvard_pilgrim: {
    primaryCriteria: "interqual",
    notes: "Harvard Pilgrim uses InterQual criteria.",
    tips: [
      "Reference Massachusetts/New England state requirements",
      "Emphasize intensity of service",
    ],
  },
  la_care: {
    primaryCriteria: "interqual",
    notes: "L.A. Care (Medicaid managed care) uses InterQual.",
    tips: [
      "Check California Medicaid (Medi-Cal) specific requirements",
      "Document social determinants",
      "Emphasize intensity of service",
    ],
  },
  health_net: {
    primaryCriteria: "mixed",
    notes: "Health Net uses both InterQual and MCG depending on product line.",
    tips: [
      "Ask which criteria applies",
      "For TRICARE West, follow TRICARE Policy Manual",
    ],
  },

  // Medicaid MCOs
  amerigroup: {
    primaryCriteria: "interqual",
    notes: "Amerigroup (Anthem) uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Document social determinants for Medicaid cases",
    ],
  },
  caresource: {
    primaryCriteria: "interqual",
    notes: "CareSource uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Strong Medicaid focus",
    ],
  },
  sunshine: {
    primaryCriteria: "interqual",
    notes: "Sunshine Health (Centene/FL) uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Florida Medicaid specific requirements may apply",
    ],
  },
  meridian: {
    primaryCriteria: "interqual",
    notes: "Meridian Health Plan uses InterQual criteria.",
    tips: [
      "Emphasize intensity of service",
      "Covers MI/IL Medicaid",
    ],
  },

  // Workers' Comp
  workers_comp_carrier: {
    primaryCriteria: "state_wc",
    notes: "Workers' compensation uses ACOEM guidelines and state-specific rules.",
    tips: [
      "Reference ACOEM (American College of Occupational and Environmental Medicine) guidelines",
      "ODG (Official Disability Guidelines) may apply",
      "Check your state workers' comp medical treatment guidelines",
      "Document work-relatedness clearly",
    ],
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Get available plan types for a specific insurer
 */
export function getAvailablePlanTypes(insurerValue: string): PlanType[] {
  const insurer = INSURANCE_COMPANIES.find((i) => i.value === insurerValue);
  if (!insurer) {
    // Return all plan types if insurer not found
    return PLAN_TYPES;
  }

  return PLAN_TYPES.filter((pt) => insurer.availablePlanTypes.includes(pt.value));
}

/**
 * Get criteria info for an insurer with fallback
 */
export function getInsurerCriteriaInfo(insurerValue: string): InsurerCriteriaInfo {
  // Check for exact match
  if (INSURER_CRITERIA_INFO[insurerValue]) {
    return INSURER_CRITERIA_INFO[insurerValue];
  }

  // Check for BCBS regional match (fall back to generic BCBS)
  if (insurerValue.startsWith("bcbs_")) {
    return (
      INSURER_CRITERIA_INFO[insurerValue] ||
      INSURER_CRITERIA_INFO.bcbs || {
        primaryCriteria: "interqual",
        notes: "Most BCBS plans use InterQual criteria.",
        tips: ["Emphasize intensity of service"],
      }
    );
  }

  // Default fallback
  return {
    primaryCriteria: "unknown",
    notes: "Criteria information not available for this insurer.",
    tips: [
      "Ask the reviewer which criteria they're using (InterQual or MCG)",
      "Prepare arguments for both severity of illness and intensity of service",
      "Reference evidence-based clinical guidelines",
    ],
  };
}

/**
 * Get insurer by value
 */
export function getInsurerByValue(value: string): InsuranceCompany | undefined {
  return INSURANCE_COMPANIES.find((i) => i.value === value);
}

/**
 * Get plan type by value
 */
export function getPlanTypeByValue(value: string): PlanType | undefined {
  return PLAN_TYPES.find((p) => p.value === value);
}

// ============================================================================
// CRITERIA TYPE INFO (for UI display)
// ============================================================================

export const CRITERIA_TYPE_INFO: Record<
  string,
  { label: string; description: string }
> = {
  interqual: {
    label: "InterQual",
    description:
      "Focuses on intensity of service - what level of care and monitoring the patient needs day-by-day.",
  },
  mcg: {
    label: "MCG (Milliman)",
    description:
      "Focuses on severity of illness - how sick the patient is based on diagnosis and clinical markers.",
  },
  mixed: {
    label: "Mixed Criteria",
    description:
      "Uses both InterQual and MCG depending on service type. Ask which criteria applies.",
  },
  cms: {
    label: "CMS (Medicare)",
    description:
      "Uses National Coverage Determinations (NCDs) and Local Coverage Determinations (LCDs). Searchable at cms.gov/medicare-coverage-database.",
  },
  tricare: {
    label: "TRICARE",
    description:
      "Uses TRICARE Policy Manual and DoD clinical guidelines.",
  },
  va: {
    label: "VA",
    description:
      "Uses VA/DoD Clinical Practice Guidelines.",
  },
  state_wc: {
    label: "Workers' Comp",
    description:
      "Uses ACOEM guidelines, ODG (Official Disability Guidelines), and state-specific rules.",
  },
  proprietary: {
    label: "Proprietary",
    description:
      "Uses internal criteria. Focus on evidence-based guidelines and clinical literature.",
  },
  unknown: {
    label: "Unknown",
    description:
      "Criteria not documented. Ask the reviewer which criteria system they use.",
  },
};